import { useLocation, Link } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { Home, Search, HelpCircle, BookOpen, Building2, MapPin, Star, ArrowRight } from "lucide-react";
import { calculateProfileCompleteness } from "@/lib/profileCompleteness";
import { Layout } from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MSPSearchBox } from "@/components/home/MSPSearchBox";
import { useMSPs } from "@/hooks/useMSPs";
import { getEmployeeSegmentLabel } from "@/lib/constants";
import { MspLogoImage } from '@/components/msp/MspLogoImage';

const NotFound = () => {
  const location = useLocation();
  const { msps, loading } = useMSPs();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  // Get top 4 MSPs by profile completeness + review count
  const popularMSPs = useMemo(() => {
    const active = msps.filter(m => m.actief !== false);
    return [...active].sort((a, b) => {
      const scoreA = calculateProfileCompleteness(a).percentage;
      const scoreB = calculateProfileCompleteness(b).percentage;
      if (scoreB !== scoreA) return scoreB - scoreA;
      return ((b as any).review_count ?? 0) - ((a as any).review_count ?? 0);
    }).slice(0, 4);
  }, [msps]);

  return (
    <Layout>
      <SEOHead 
        title="Pagina niet gevonden | MSP-Vergelijker.nl"
        description="Deze pagina bestaat niet of is verplaatst. Ga terug naar de homepage of zoek een MSP."
        noindex={true}
      />
      <div className="container mx-auto px-4 py-20">
        {/* Main 404 Content */}
        <div className="text-center mb-12">
          <h1 className="text-7xl font-bold text-primary mb-4">404</h1>
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            Pagina niet gevonden
          </h2>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            De pagina die je zoekt bestaat niet of is verplaatst. 
            Gebruik een van de onderstaande opties om verder te gaan.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg">
              <Link to="/">
                <Home className="h-4 w-4 mr-2" />
                Naar homepage
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/vergelijken">
                <Search className="h-4 w-4 mr-2" />
                MSP's zoeken
              </Link>
            </Button>
            <Button variant="ghost" size="lg" asChild>
              <Link to="/faq">
                <HelpCircle className="h-4 w-4 mr-2" />
                Veelgestelde vragen
              </Link>
            </Button>
            <Button variant="ghost" size="lg" asChild>
              <Link to="/academy">
                <BookOpen className="h-4 w-4 mr-2" />
                Academy
              </Link>
            </Button>
          </div>
        </div>

        {/* Search Box */}
        <div className="max-w-xl mx-auto mb-12">
          <p className="text-center text-muted-foreground mb-4">
            Of zoek direct naar een MSP:
          </p>
          <MSPSearchBox msps={msps} />
        </div>

        {/* Popular MSPs */}
        {!loading && popularMSPs.length > 0 && (
          <div className="max-w-4xl mx-auto">
            <h3 className="text-lg font-semibold text-center mb-6">
              Populaire MSP's
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {popularMSPs.map(msp => {
                const diensten = (msp as any).diensten || [];
                const doelgroep = (msp as any).doelgroep_segment || [];
                const topServices = diensten.slice(0, 2);
                const topDoelgroep = doelgroep.slice(0, 1);

                return (
                  <Link key={msp.id} to={`/msp/${msp.slug}`} className="group">
                    <Card className="hover:shadow-md transition-shadow h-full flex flex-col">
                      <CardContent className="p-4 flex flex-col flex-1">
                        {/* Header: logo + name + city */}
                        <div className="flex items-center gap-3 mb-3">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                            <MspLogoImage
                              logoUrl={msp.logo_url}
                              variant="card"
                              alt={msp.naam || ''}
                              className="h-8 w-8 object-contain rounded"
                              loading="lazy"
                              width={32}
                              height={32}
                              fallback={<Building2 className="h-5 w-5 text-primary" />}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate text-foreground">{msp.naam}</p>
                            <div className="flex items-center text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3 mr-1 shrink-0" />
                              <span className="truncate">{msp.plaats}</span>
                            </div>
                          </div>
                        </div>

                        {/* Rating */}
                        {msp.average_rating && msp.average_rating > 0 && (
                          <div className="flex items-center text-xs text-muted-foreground mb-3">
                            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 mr-1" />
                            {msp.average_rating.toFixed(1)}
                            {msp.review_count && msp.review_count > 0 && (
                              <span className="ml-1">({msp.review_count})</span>
                            )}
                          </div>
                        )}

                        {/* Chips: services + doelgroep */}
                        {(topServices.length > 0 || topDoelgroep.length > 0) && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {topServices.map((s: string) => (
                              <Badge key={s} variant="secondary" className="text-[10px] px-1.5 py-0">
                                {s}
                              </Badge>
                            ))}
                            {topDoelgroep.map((d: string) => (
                              <Badge key={d} variant="outline" className="text-[10px] px-1.5 py-0">
                                {getEmployeeSegmentLabel(d)}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {/* CTA */}
                        <div className="mt-auto pt-2">
                          <span className="text-xs text-primary font-medium group-hover:underline flex items-center gap-1">
                            Bekijk profiel
                            <ArrowRight className="h-3 w-3" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default NotFound;
